// components/UniverseSelector.jsx
//
// Pick the symbol universe a scanner profile watches.
// Three modes:
//   - top_volume : top N by 24h quote volume from selected exchange/market
//   - list       : explicit symbol list (paste/select)
//   - category   : preset categories (memes, defi, layer1, etc.)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, Slider, Chip,
  CircularProgress, IconButton, Stack, Tooltip,
} from '@mui/material';
import {
  TrendingUp, FormatListBulleted, Category, Refresh, Close,
} from '@mui/icons-material';
import scannerApi from '../services/scannerApi';
import { Collapse } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
/* ═══════════════════════════════════════════════════════════════════════════════
   THEME
   ═══════════════════════════════════════════════════════════════════════════════ */

const C = {
  bg0: '#070a0f', bg1: '#070a0f', bg2: '#0f1218', bg3: '#0f1218', bg4: '#0b0e15',
  b0: 'rgba(255,255,255,0.06)', b1: 'rgba(255,255,255,0.06)', b2: 'rgba(255,255,255,0.1)',
  t1: '#f2f6ff', t2: '#e8edf7', t3: '#9aa6bd', t4: '#6f7c96',
  green: '#46b88a', red: '#d97b72', blue: '#4d8fc7', yellow: '#d9a441', purple: '#4d8fc7',
  longDim: 'rgba(70,184,138,0.08)', longBdr: 'rgba(70,184,138,0.25)',
  mono: 'Roboto, "Helvetica Neue", Arial, sans-serif',
};

const panelSx = {
  bgcolor: C.bg3, border: '1px solid', borderColor: C.b0, borderRadius: 2, overflow: 'hidden',
};

const headerBarSx = {
  display: 'flex', alignItems: 'center',
  px: 2, py: 1, bgcolor: C.bg1,
  borderBottom: '1px solid', borderColor: C.b0, minHeight: 40,
};

const microLabel = {
  fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  lineHeight: 1, mb: '5px', display: 'block',
};

const fieldSx = {
  bgcolor: C.bg1, borderRadius: 1.5,
  '& fieldset': { borderColor: C.b0, borderRadius: 1.5 },
  '&:hover fieldset': { borderColor: `${C.b2} !important` },
  '&.Mui-focused fieldset': { borderColor: `${C.b2} !important`, borderWidth: '1px !important' },
};


/* ═══════════════════════════════════════════════════════════════════════════════
   UNIVERSE MODES
   ═══════════════════════════════════════════════════════════════════════════════ */

const UNIVERSE_MODES = [
  { value: 'top_volume', label: 'Top Volume',  icon: TrendingUp,        desc: 'Top N symbols by 24h volume — refreshed automatically' },
  { value: 'list',       label: 'Symbol List', icon: FormatListBulleted, desc: 'Explicit list of symbols you specify' },
  { value: 'category',   label: 'Category',    icon: Category,           desc: 'Curated list — memes, defi, l1, etc.' },
];


const CATEGORIES = [
  { value: 'meme',    label: 'Memes',    desc: 'High-volatility meme coins' },
  { value: 'defi',     label: 'DeFi',     desc: 'Decentralized finance protocols' },
  { value: 'layer1',   label: 'Layer 1',  desc: 'Base-layer blockchains' },
  { value: 'layer2',   label: 'Layer 2',  desc: 'Scaling solutions' },
  { value: 'ai',       label: 'AI',       desc: 'AI-related tokens' },
  { value: 'gaming',   label: 'Gaming',   desc: 'Gaming & metaverse' },
];


/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════════════════════════════════════════ */

function PreviewSummary({ preview, loading, error, onRefresh }) {
  const [expanded, setExpanded] = useState(false);

  // ── Collapsed-row content ──────────────────────────────────
  const summaryText = (() => {
    if (loading)               return 'Loading…';
    if (error)                 return error;
    if (!preview)              return 'No preview yet';
    if (preview.count === 0)   return 'No matching symbols';

    const symbols = preview.symbols || [];
    const sample = symbols.slice(0, 6).map(s => s.symbol).join(', ');
    const more = symbols.length > 6 ? `, +${symbols.length - 6} more` : '';
    return `${sample}${more}`;
  })();

  const accentColor = (() => {
    if (error)                       return C.red;
    if (!preview || loading)         return C.t4;
    if (preview.count === 0)         return C.yellow;
    return C.green;
  })();

  return (
    <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: C.b0 }}>
      {/* ── Collapsed summary row ───────────────────────────── */}
      <Box
        onClick={() => preview?.count > 0 && setExpanded(e => !e)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1.25, py: '8px',
          bgcolor: C.bg1,
          border: '1px solid',
          borderColor: expanded ? C.b2 : C.b0,
          borderRadius: 1.5,
          cursor: preview?.count > 0 ? 'pointer' : 'default',
          transition: 'all 0.15s',
          '&:hover': preview?.count > 0 ? { borderColor: C.b2 } : {},
        }}
      >
        {/* Status dot */}
        <Box
          sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: accentColor,
            flexShrink: 0,
            opacity: loading ? 0.4 : 1,
            animation: loading ? 'pulse 1.4s ease-in-out infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.4 },
              '50%':      { opacity: 1 },
            },
          }}
        />

        {/* Count + summary */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          {preview && !error && (
            <Typography sx={{
              fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1,
              flexShrink: 0,
            }}>
              {preview.count}
            </Typography>
          )}
          <Typography
            sx={{
              fontFamily: C.mono, fontSize: 10.5, color: error ? C.red : C.t3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flex: 1, minWidth: 0,
            }}
          >
            {summaryText}
          </Typography>
        </Box>

        {/* Refresh button */}
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onRefresh(); }}
          sx={{ p: '3px', color: C.t4, flexShrink: 0 }}
          disabled={loading}
        >
          <Refresh sx={{
            fontSize: 13,
            animation: loading ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': {
              '0%':   { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }} />
        </IconButton>

        {/* Expand chevron — only when there's data */}
        {preview?.count > 0 && (
          <Box sx={{ color: C.t4, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {expanded
              ? <KeyboardArrowUp sx={{ fontSize: 16 }} />
              : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
          </Box>
        )}
      </Box>

      {/* ── Expanded chip grid ─────────────────────────────── */}
      <Collapse in={expanded && preview?.count > 0}>
        <Box
          sx={{
            mt: 1,
            display: 'flex', flexWrap: 'wrap', gap: 0.5,
            maxHeight: 240, overflow: 'auto',
            p: 1.25,
            bgcolor: C.bg1,
            border: '1px solid', borderColor: C.b0,
            borderRadius: 1.5,
          }}
        >
          {(preview?.symbols || []).slice(0, 200).map(s => (
            <Chip
              key={s.symbol}
              size="small"
              label={s.symbol}
              sx={{
                height: 22,
                fontFamily: C.mono, fontSize: 10, fontWeight: 600,
                bgcolor: C.bg3, color: C.t2,
                border: '1px solid', borderColor: C.b0,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ))}
          {(preview?.symbols || []).length > 200 && (
            <Typography sx={{
              fontFamily: C.mono, fontSize: 10, color: C.t4,
              alignSelf: 'center', ml: 1,
            }}>
              +{preview.symbols.length - 200} more
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function UniverseSelector({
  universe,           // { type, ...mode-specific-fields }
  onUniverseChange,
  marketType = 'spot',
}) {
  const u = universe || { type: 'top_volume', limit: 100, quote: 'USDT', min_volume: 0 };
  const mode = u.type || 'top_volume';

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Patch helper
  const patch = (fields) => onUniverseChange({ ...u, ...fields });

  // Fetch preview when top_volume params change
  const fetchPreview = useCallback(async () => {
  if (mode === 'list') return;  // list mode shows the symbols inline already

  if (!scannerApi?.universe?.preview) {
    setPreviewError('Universe preview API not configured');
    setPreview(null);
    return;
  }

  setPreviewLoading(true);
  setPreviewError(null);

  try {
    const payload = {
      type: mode,
      market_type: marketType,
      blacklist: u.blacklist || [],
      fee_filter: u.fee_filter || 'any',
    };

    if (mode === 'top_volume') {
      payload.limit = u.limit || 100;
      payload.quote = u.quote || 'USDT';
      payload.min_volume = u.min_volume || 0;
    } else if (mode === 'category') {
      payload.category = u.category;
    }

    const r = await scannerApi.universe.preview(payload);
    setPreview(r);
  } catch (err) {
    setPreviewError(err?.response?.data?.error || err.message || 'Preview failed');
    setPreview(null);
  } finally {
    setPreviewLoading(false);
  }
}, [mode, marketType, u.limit, u.quote, u.min_volume, u.category, u.fee_filter, u.blacklist]);

  // Auto-preview on mount + param change (debounced)
useEffect(() => {
  if (mode === 'list') return;  // skip list mode
  const t = setTimeout(fetchPreview, 400);
  return () => clearTimeout(t);
}, [fetchPreview, mode]);

useEffect(() => {
  setPreview(null);
  setPreviewError(null);
}, [mode]);


  // ── Mode change ────────────────────────────────────────────────────────────
const handleModeChange = (newMode) => {
  const preserved = {
    blacklist: u.blacklist,
    fee_filter: u.fee_filter,  // ← carry through
  };

  if (newMode === 'top_volume') {
    onUniverseChange({ type: 'top_volume', limit: 100, quote: 'USDT', min_volume: 0, ...preserved });
  } else if (newMode === 'list') {
    onUniverseChange({ type: 'list', symbols: [], ...preserved });
  } else if (newMode === 'category') {
    onUniverseChange({ type: 'category', category: 'memes', ...preserved });
  }
};


  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={headerBarSx}>
        <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: C.blue, mr: 1, opacity: 0.8 }} />
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
          UNIVERSE
        </Typography>
<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
  {u.fee_filter && u.fee_filter !== 'any' && (
    <Chip
      size="small"
      label={
        u.fee_filter === 'zero_maker'  ? 'ZERO MAKER'  :
        u.fee_filter === 'zero_taker'  ? 'ZERO TAKER'  :
        u.fee_filter === 'zero_either' ? 'ZERO EITHER' :
        u.fee_filter === 'zero_both'   ? 'ZERO BOTH'   : ''
      }
      sx={{
        height: 18,
        fontFamily: C.mono, fontSize: 9, fontWeight: 700,
        bgcolor: C.longDim, color: C.green,
        border: '1px solid',
        borderColor: 'rgba(70,184,138, 0.4)',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  )}
  {preview && mode !== 'list' && (
    <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3 }}>
      {preview.count} symbols
    </Typography>
  )}
  {mode === 'list' && (
    <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3 }}>
      {(u.symbols || []).length} symbols
    </Typography>
  )}
</Box>
      </Box>

      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Mode selector cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          {UNIVERSE_MODES.map(m => {
            const Icon = m.icon;
            const active = mode === m.value;
            return (
              <Box
  key={m.value}
  onClick={() => handleModeChange(m.value)}
  sx={{
    px: 1.5, py: 1.25,
    border: '1px solid',
    borderColor: active ? C.b2 : C.b0,
    borderRadius: 1.5, cursor: 'pointer',
    bgcolor: active ? C.bg4 : 'transparent',
    transition: 'all 0.15s',
    '&:hover': { borderColor: C.b2, bgcolor: C.bg4 },
  }}
>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
    <Icon sx={{ fontSize: 13, color: active ? C.t2 : C.t4 }} />
    <Typography sx={{
      fontFamily: C.mono, fontSize: 11, fontWeight: 700,
      color: active ? C.t1 : C.t3, letterSpacing: '0.02em',
    }}>
      {m.label}
    </Typography>
  </Box>
  <Typography sx={{ fontFamily: C.mono, fontSize: 9.5, color: C.t4, lineHeight: 1.4 }}>
    {m.desc}
  </Typography>
</Box>
            );
          })}
        </Box>

        {/* ─── TOP VOLUME MODE ─── */}
        {mode === 'top_volume' && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography sx={microLabel}>QUOTE CURRENCY</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: C.bg1, borderRadius: 1.5, border: '1px solid', borderColor: C.b0 }}>
                  {['USDT', 'USDC', 'BTC'].map(q => {
                    const active = (u.quote || 'USDT') === q;
                    return (
                      <Box
                        key={q}
                        onClick={() => patch({ quote: q })}
                        sx={{
                          flex: 1, textAlign: 'center', px: 1, py: '5px',
                          borderRadius: 1, cursor: 'pointer',
                          bgcolor: active ? C.bg4 : 'transparent',
                          border: '1px solid', borderColor: active ? C.b2 : 'transparent',
                          '&:hover': { bgcolor: C.bg4, borderColor: C.b2 },
                        }}
                      >
                        <Typography sx={{
                          fontFamily: C.mono, fontSize: 11, fontWeight: active ? 700 : 500,
                          color: active ? C.t1 : C.t4,
                        }}>
                          {q}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box>
                <Typography sx={microLabel}>MIN 24H VOLUME (USD)</Typography>
                <TextField
                  size="small" type="number" fullWidth
                  value={u.min_volume ?? 0}
                  onChange={e => patch({ min_volume: Math.max(0, Number(e.target.value) || 0) })}
                  placeholder="0"
                  inputProps={{ step: 100_000, min: 0, style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
                  sx={{ '& .MuiInputBase-root': { ...fieldSx, height: 38 } }}
                />
              </Box>
            </Box>

            <Box>
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
    <Typography sx={microLabel}>UNIVERSE SIZE</Typography>
    <Typography sx={{ fontFamily: C.mono, fontSize: 16, fontWeight: 700, color: C.t1 }}>
      Top {u.limit || 100}
    </Typography>
  </Box>
  <Slider
    value={u.limit || 100}
    onChange={(_, v) => patch({ limit: v })}
    min={10} max={500} step={10}
    marks={[
      { value: 10, label: '10' },
      { value: 100, label: '100' },
      { value: 250, label: '250' },
      { value: 500, label: '500' },
    ]}
    sx={{
      color: C.t3,
      '& .MuiSlider-thumb': { width: 14, height: 14, borderRadius: 1.5 },
      '& .MuiSlider-track': { height: 3 },
      '& .MuiSlider-rail': { height: 3, bgcolor: C.b0, opacity: 1 },
      '& .MuiSlider-markLabel': { fontFamily: C.mono, fontSize: 10, color: C.t4 },
    }}
  />
</Box>

            {/* Preview */}
            {/* Preview — collapsible single-row summary */}

          </>
        )}

        {/* ─── LIST MODE ─── */}
        {mode === 'list' && (
          <ListModePanel
            symbols={u.symbols || []}
            onChange={(symbols) => patch({ symbols })}
          />
        )}

        {/* ─── CATEGORY MODE ─── */}
        {/* ─── CATEGORY MODE ─── */}
{mode === 'category' && (
  <Box>
    <Typography sx={microLabel}>CATEGORY</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {CATEGORIES.map(cat => {
        const active = u.category === cat.value;
        return (
          <Tooltip key={cat.value} title={cat.desc} arrow>
            <Box
              onClick={() => patch({ category: cat.value })}
              sx={{
                px: 1.5, py: 1,
                border: '1px solid',
                borderColor: active ? C.b2 : C.b0,
                borderRadius: 1.5, cursor: 'pointer',
                bgcolor: active ? C.bg4 : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { borderColor: C.b2, bgcolor: C.bg4 },
              }}
            >
              <Typography sx={{
                fontFamily: C.mono, fontSize: 12, fontWeight: 600,
                color: active ? C.t1 : C.t3,
              }}>
                {cat.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  </Box>
)}

{mode !== 'list' && (
  <PreviewSummary
    preview={preview}
    loading={previewLoading}
    error={previewError}
    onRefresh={fetchPreview}
  />
)}

{/* Universal filters block (fee filter + blacklist) */}
<Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: C.b0, /* ... */ }}>
  {/* fee filter + blacklist */}
</Box>

        {/* Blacklist (always shown) */}
        {/* Universal filters — applied across all universe modes */}
{/* Universal filters */}
<Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: C.b0, display: 'flex', flexDirection: 'column', gap: 2 }}>

  {/* Fee filter */}
  <Box>
    <Typography sx={microLabel}>FEE FILTER</Typography>

    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: 0.5,
      p: 0.5,
      bgcolor: C.bg1,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: C.b0,
    }}>
      {[
        { value: 'any',         label: 'Any',          desc: 'No fee filter' },
        { value: 'zero_maker',  label: 'Zero Maker',   desc: 'Maker fee = 0 (limit orders free)' },
        { value: 'zero_taker',  label: 'Zero Taker',   desc: 'Taker fee = 0 (market orders free)' },
        { value: 'zero_either', label: 'Zero Either',  desc: 'Either maker or taker = 0' },
        { value: 'zero_both',   label: 'Zero Both',    desc: 'Both maker AND taker = 0' },
      ].map(opt => {
        const active = (u.fee_filter || 'any') === opt.value;
        return (
          <Tooltip key={opt.value} title={opt.desc} arrow placement="top">
            <Box
              onClick={() => patch({ fee_filter: opt.value })}
              sx={{
                px: 0.75, py: '6px',
                textAlign: 'center',
                borderRadius: 1, cursor: 'pointer',
                bgcolor: active ? C.bg4 : 'transparent',
                border: '1px solid', borderColor: active ? C.b2 : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: C.bg4, borderColor: C.b2 },
              }}
            >
              <Typography sx={{
                fontFamily: C.mono,
                fontSize: 10.5,
                fontWeight: active ? 700 : 500,
                color: active ? (opt.value === 'any' ? C.t1 : C.green) : C.t4,
                whiteSpace: 'nowrap',
              }}>
                {opt.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>

    
  </Box>

  {/* Blacklist */}
  <Box>
    <Typography sx={microLabel}>BLACKLIST</Typography>
    <SymbolListInput
      symbols={u.blacklist || []}
      onChange={(blacklist) => patch({ blacklist })}
      placeholder="Examples: SHIBUSDT, FLOKIUSDT"
    />
  </Box>
</Box>
      </Box>
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   LIST MODE PANEL
   ═══════════════════════════════════════════════════════════════════════════════ */

function ListModePanel({ symbols, onChange }) {
  return (
    <Box>
      <Typography sx={microLabel}>SYMBOLS TO SCAN</Typography>
      <SymbolListInput
        symbols={symbols}
        onChange={onChange}
        placeholder="Examples: BTCUSDT, ETHUSDT"
      />
    </Box>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   SYMBOL LIST INPUT — paste/edit + chip display
   ═══════════════════════════════════════════════════════════════════════════════ */

function SymbolListInput({ symbols, onChange, placeholder }) {
  const [input, setInput] = useState('');

  const commit = () => {
    if (!input.trim()) return;
    const tokens = input
      .split(/[\s,;\n]+/)
      .map(s => s.trim().toUpperCase())
      .filter(s => s && /^[A-Z0-9]+$/.test(s));
    const next = [...new Set([...symbols, ...tokens])];
    onChange(next);
    setInput('');
  };

  const remove = (sym) => onChange(symbols.filter(s => s !== sym));

  return (
    <Box>
      <TextField
        size="small" fullWidth
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
        placeholder={placeholder}
        inputProps={{ style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
        sx={{ '& .MuiInputBase-root': { ...fieldSx, height: 38 } }}
      />

      {symbols.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {symbols.map(s => (
            <Chip
              key={s}
              size="small"
              label={s}
              onDelete={() => remove(s)}
              deleteIcon={<Close sx={{ fontSize: 12 }} />}
              sx={{
                height: 22,
                fontFamily: C.mono, fontSize: 10, fontWeight: 600,
                bgcolor: C.bg1, color: C.t2,
                border: '1px solid', borderColor: C.b0,
                '& .MuiChip-label': { px: 0.75 },
                '& .MuiChip-deleteIcon': { color: C.t4, fontSize: 12, '&:hover': { color: C.red } },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}